import { useState } from "react";
import { observer } from "mobx-react-lite";
import type { PMIssue } from "@/domains/pm/types";
import type { GroupedIssueItem } from "@/domains/pm/utils/groupAndSort";
import type { ViewerDocumentData } from "@/domains/documents/hooks/useDocumentViewer";
import { IssueRow } from "./IssueRow";
import { cn } from "@/lib/utils";

interface IssueGroupProps {
  label: string;
  count: number;
  items: GroupedIssueItem[];
  defaultOpen?: boolean;
  selectedIssueId?: number;
  onSelectIssue: (issue: PMIssue) => void;
  /** Called when the user clicks an orphan-parent chip on a subissue
   *  row — resolves the id back to a loaded issue and selects it. */
  onJumpToIssue?: (id: number) => void;
  buildingShortCode?: string;
  /** Number of columns in the shared table — drives colSpan for the
   *  group header row. */
  colSpan: number;
  openViewer: (doc: ViewerDocumentData) => Promise<void>;
  /** Ids currently checked for bulk actions (WHA-3334). */
  selectedIssueIds: ReadonlySet<number>;
  /** WHA-3456: `shiftKey` selects the range from the anchor row. */
  onMultiSelectToggle: (issueId: number, shiftKey: boolean) => void;
}

/**
 * One <tbody> in the shared list table (WHA-3315). Renders a group
 * header row + the issues belonging to this group. The header / colgroup /
 * column widths are owned by the parent IssuesList so every row in every
 * group participates in the same column sizing pass.
 *
 * WHA-3644: selecting a row no longer expands it inline — the issue detail
 * opens in the floating overlay panel (IssueDetailPanel) instead. The row is
 * just highlighted via `isSelected`.
 */
export const IssueGroup = observer(function IssueGroup({
  label,
  count,
  items,
  defaultOpen = true,
  selectedIssueId,
  onSelectIssue,
  onJumpToIssue,
  buildingShortCode,
  colSpan,
  openViewer,
  selectedIssueIds,
  onMultiSelectToggle,
}: IssueGroupProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <tbody>
      {/* Group header row */}
      <tr
        className="cursor-pointer transition-colors hover:bg-muted/40"
        onClick={() => setIsOpen((v) => !v)}
      >
        <td colSpan={colSpan} className="px-1 py-2.5">
          <div className="flex items-center gap-2">
            <span className="w-3 select-none text-[11px] text-muted-foreground">
              {isOpen ? "▾" : "▸"}
            </span>
            <span className="text-sm font-medium">{label}</span>
            <span
              className={cn(
                "inline-flex h-[18px] min-w-[20px] items-center justify-center rounded-full px-1.5 text-[10px] font-semibold",
                count > 0 ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
              )}
            >
              {count}
            </span>
          </div>
        </td>
      </tr>

      {isOpen &&
        items.map(({ issue, indentLevel, orphanedParentRef }) => (
          <IssueRow
            key={issue.id}
            issue={issue}
            isSelected={selectedIssueId === issue.id}
            onSelect={onSelectIssue}
            buildingShortCode={buildingShortCode}
            openViewer={openViewer}
            isMultiSelected={selectedIssueIds.has(issue.id)}
            onMultiSelectToggle={onMultiSelectToggle}
            selectedIssueIds={selectedIssueIds}
            indentLevel={indentLevel}
            orphanedParentRef={orphanedParentRef}
            onJumpToIssue={onJumpToIssue}
          />
        ))}
    </tbody>
  );
});
